import React, { useContext } from 'react'
import { observer } from 'mobx-react-lite'
import { DraftShareContext } from '../../../contexts/DraftShareContext'
import { LocaleContext } from '../../../contexts/LocaleContext'
import LocaleToggle from '../../Shared/LocaleToggle'
import DraftStatusBadge from '../../Shared/DraftStatusBadge'
import { PROVIDER_ICONS, PROVIDER_COLORS, actionTypeLabel } from '../../../utils/actionDisplay'
import { clientFeedbackSentOverride } from '../../../utils/draftStatus'
import SubmissionForm from './SubmissionForm'
import ClientReview from './ClientReview'

const ActionListItem = observer(function ActionListItem({ action, role, isSelected, onSelect }) {
  const Icon = PROVIDER_ICONS[action.provider]
  const draftStatus = action.action_draft?.draft_status
  const statusOverride = clientFeedbackSentOverride(role, draftStatus, action.action_draft?.latestVersion?.client_feedback_submitted)

  return (
    <div
      onClick={onSelect}
      className={`flex items-center gap-2 p-2 rounded-lg mb-1 cursor-pointer ${
        isSelected
          ? 'border-2 border-rb-green dark:border-rb-green bg-rb-soft-green dark:bg-rb-dark-mode-lighter'
          : 'border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter hover:bg-gray-50 dark:hover:bg-rb-dark-mode-lighter'
      }`}
    >
      {Icon && <Icon className={`shrink-0 ${PROVIDER_COLORS[action.provider]}`} />}
      <span className="text-sm truncate dark:text-white">
        {action.name} <span className="text-rb-mat-grey dark:text-rb-dark-mode-text-grey">- {actionTypeLabel(action.action_type)}</span>
      </span>
      <DraftStatusBadge draftStatus={draftStatus} role={role} className="ml-auto shrink-0" {...statusOverride} />
    </div>
  )
})

export default observer(function DraftSharePortal() {
  const { state } = useContext(DraftShareContext)
  const { localeState } = useContext(LocaleContext)
  const isClient = state.role === 'client'

  return (
    <div className="flex h-screen bg-rb-grey dark:bg-rb-dark-mode-background">
      <div className="w-[280px] h-full border-2 border-rb-widget-border dark:border-rb-dark-mode-lighter bg-white dark:bg-rb-dark-mode overflow-y-auto p-4">
        <span className="font-bold text-lg dark:text-white">Reachbird</span>
        <p className="font-bold mt-2 dark:text-white">{state.campaignName}</p>

        <div className="my-4">
          <LocaleToggle locale={localeState.locale} onChange={localeState.setLocale} />
        </div>

        <div className="flex justify-between items-center mb-2">
          <h3 className="font-bold dark:text-white">Campaign requests</h3>
          <span className="text-rb-mat-grey dark:text-rb-dark-mode-text-grey">{state.actions.length}</span>
        </div>

        {isClient ? (
          state.actions.length === 0 ? (
            <p className="text-sm text-rb-mat-grey dark:text-rb-dark-mode-text-grey">Nothing is currently awaiting your review.</p>
          ) : (
            state.actionsByInfluencer.map(group => (
              <div key={group.influencerId} className="mb-3">
                <p className="text-sm font-bold mb-2 dark:text-white">{group.influencerName}</p>
                {group.actions.map(action => (
                  <ActionListItem
                    key={action.id}
                    action={action}
                    role="client"
                    isSelected={state.selectedActionId === action.id}
                    onSelect={() => state.selectAction(action.id)}
                  />
                ))}
              </div>
            ))
          )
        ) : (
          <>
            <p className="text-sm font-bold mb-2 dark:text-white">{state.influencerName}</p>
            {state.actions.map(action => (
              <ActionListItem
                key={action.id}
                action={action}
                role="influencer"
                isSelected={state.selectedActionId === action.id}
                onSelect={() => state.selectAction(action.id)}
              />
            ))}
          </>
        )}
      </div>

      <div className="flex-1 overflow-y-auto flex flex-col items-center p-5">
        {state.selectedAction ? (
          isClient ? <ClientReview action={state.selectedAction} /> : <SubmissionForm action={state.selectedAction} />
        ) : (
          <p className="dark:text-white">Select a request from the list</p>
        )}
      </div>
    </div>
  )
})
